import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { Facebook, Instagram, MessageCircle, Phone, ChevronDown, ChevronUp } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { db } from '../../lib/firebase';
import VideoBlock from '../../components/SalePageBlocks/VideoBlock';
import ImageBlock from '../../components/SalePageBlocks/ImageBlock';
import RichTextBlock from '../../components/SalePageBlocks/RichTextBlock';
import YouTubeBlock from '../../components/SalePageBlocks/YouTubeBlock';
import CTAButtonBlock from '../../components/SalePageBlocks/CTAButtonBlock';
import PriceBlock from '../../components/SalePageBlocks/PriceBlock';
import ReviewBlock from '../../components/SalePageBlocks/ReviewBlock';
import FAQBlock from '../../components/SalePageBlocks/FAQBlock';
import CountdownBlock from '../../components/SalePageBlocks/CountdownBlock';
import ContactFormBlock from '../../components/SalePageBlocks/ContactFormBlock';
import GalleryBlock from '../../components/SalePageBlocks/GalleryBlock';
import ComparisonTableBlock from '../../components/SalePageBlocks/ComparisonTableBlock';
import IconTextBlock from '../../components/SalePageBlocks/IconTextBlock';
import DividerBlock from '../../components/SalePageBlocks/DividerBlock';
import SpacerBlock from '../../components/SalePageBlocks/SpacerBlock';
import PaymentBlock from '../../components/SalePageBlocks/PaymentBlock';

// Sellers type just a username (e.g. "viitamint.thanat"); build the real profile URL from it.
// A value that's already a full URL (saved before this change, or pasted deliberately) passes through untouched.
const socialProfileUrl = (platform: 'facebook' | 'instagram' | 'line', raw?: string) => {
  const v = (raw || '').trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  const handle = v.replace(/^[@/]+/, '');
  if (!handle) return null;
  if (platform === 'facebook') return `https://facebook.com/${handle}`;
  if (platform === 'instagram') return `https://instagram.com/${handle}`;
  return `https://line.me/ti/p/@${handle}`;
};

interface ContentBlock {
  id: string;
  type: 'video' | 'image' | 'text' | 'youtube' | 'social' | 'cta' | 'price' | 'review' | 'faq' | 'countdown' | 'contact' | 'gallery' | 'comparison' | 'icontext' | 'divider' | 'spacer' | 'payment';
  data: any;
}

interface SocialLinks {
  facebook?: string;
  instagram?: string;
  line?: string;
  phone?: string;
}

interface SalePageData {
  title: string;
  slug: string;
  blocks: ContentBlock[];
  backgroundColor: string;
  socialLinks: SocialLinks;
  userId: string;
}

const SalePagePublic: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const id = searchParams.get('id');
  
  const [salePageData, setSalePageData] = useState<SalePageData | null>(null);
  const [salePageId, setSalePageId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [fabOpen, setFabOpen] = useState(true);

  useEffect(() => {
    const fetchSalePage = async () => {
      try {
        // Get username from subdomain or path
        const hostname = window.location.hostname;
        const pathname = window.location.pathname;
        let username: string | null = null;

        // Check if subdomain (e.g., viitamint.edumi.co/sale)
        if (hostname.includes('.edumi.co') && !hostname.startsWith('www') && !hostname.startsWith('salepage')) {
          username = hostname.split('.')[0];
        }
        // Check if path URL (e.g., edumi.co/@viitamint/sale)
        else if (pathname.startsWith('/@')) {
          const pathParts = pathname.split('/');
          if (pathParts[1].startsWith('@')) {
            username = pathParts[1].substring(1); // Remove @ symbol
          }
        }

        if (id) {
          // Fetch by ID (for direct links)
          const docRef = doc(db, 'salepages', id);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            setSalePageData(docSnap.data() as SalePageData);
            setSalePageId(id);
          } else {
            setNotFound(true);
          }
        } else if (slug) {
          // Check if slug is actually a userId (Firebase UIDs are typically 28 characters)
          // If slug length > 20, treat it as userId
          if (slug.length > 20) {
            // Fetch by userId (for saledi.web.app/s/{userId})
            const q = query(
              collection(db, 'salepages'),
              where('userId', '==', slug)
            );
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
              setSalePageData(querySnapshot.docs[0].data() as SalePageData);
              setSalePageId(querySnapshot.docs[0].id);
            } else {
              setNotFound(true);
            }
          } else {
            // Fetch by slug (for saledi.web.app/s/{slug})
            const q = query(
              collection(db, 'salepages'),
              where('slug', '==', slug)
            );
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
              setSalePageData(querySnapshot.docs[0].data() as SalePageData);
              setSalePageId(querySnapshot.docs[0].id);
            } else {
              setNotFound(true);
            }
          }
        } else if (username) {
          // Fetch by username (for username.edumi.co/sale or edumi.co/@username/sale)
          // First, get user ID from users collection
          const usersQuery = query(
            collection(db, 'users'),
            where('username', '==', username)
          );
          const usersSnapshot = await getDocs(usersQuery);
          
          if (!usersSnapshot.empty) {
            const userId = usersSnapshot.docs[0].id;
            
            // Then get sale page by userId
            const salepagesQuery = query(
              collection(db, 'salepages'),
              where('userId', '==', userId)
            );
            const salepagesSnapshot = await getDocs(salepagesQuery);
            
            if (!salepagesSnapshot.empty) {
              setSalePageData(salepagesSnapshot.docs[0].data() as SalePageData);
              setSalePageId(salepagesSnapshot.docs[0].id);
            } else {
              setNotFound(true);
            }
          } else {
            setNotFound(true);
          }
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error('Error fetching sale page:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchSalePage();
  }, [id, slug, location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (notFound || !salePageData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
          <p className="text-gray-600 mb-8">ไม่พบหน้าที่คุณกำลังมองหา</p>
          <a
            href="https://coursedee.web.app"
            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-semibold hover:shadow-xl transition-all"
          >
            กลับหน้าหลัก
          </a>
        </div>
      </div>
    );
  }

  // Freemium: a page that has not been paid for / published is not shown publicly.
  // (Legacy pages have no flag or isPublished:true, so only an explicit false gates.)
  if ((salePageData as any).isPublished === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F3F8F8] px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-[#EAF6F5] flex items-center justify-center mx-auto mb-5">
            <span style={{ fontSize: 30 }}>🚧</span>
          </div>
          <h1 className="text-2xl font-bold text-[#0E3A40] mb-2">หน้านี้ยังไม่เผยแพร่</h1>
          <p className="text-[#5B7D80] mb-8">เจ้าของเพจยังไม่ได้เผยแพร่หน้านี้ กรุณากลับมาใหม่อีกครั้ง</p>
          <a href="https://saledi.web.app" className="px-6 py-3 bg-gradient-to-r from-[#00B4A1] to-[#3A93B9] text-white rounded-xl font-semibold hover:shadow-xl transition-all">
            สร้าง Sale Page ของคุณ
          </a>
        </div>
      </div>
    );
  }

  const hasAnyLink = Object.values(salePageData.socialLinks || {}).some((v) => v && String(v).trim() !== '');

  return (
    <div
      style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: salePageData.backgroundColor || '#F3F8F8', fontFamily: '"IBM Plex Sans Thai", system-ui, sans-serif' }}
      onContextMenu={(e: React.MouseEvent) => e.preventDefault()}
      onDragStart={(e: React.DragEvent) => e.preventDefault()}
    >
        <Helmet>
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
          <link href="https://fonts.googleapis.com/css2?family=Anuphan:wght@600;700&family=IBM+Plex+Sans+Thai:wght@400;500;600&display=swap" rel="stylesheet" />
        </Helmet>

        <main style={{ flex: 1, maxWidth: 720, margin: '0 auto', width: '100%', padding: 'clamp(32px,6vw,56px) clamp(18px,4vw,24px) clamp(28px,4vw,36px)', display: 'flex', flexDirection: 'column', gap: 'clamp(20px,3vw,28px)' }}>
          {/* Content Blocks */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(20px,3vw,28px)' }}>
            {salePageData.blocks.map((block) => (
              <div key={block.id}>
              {block.type === 'video' && (
                <VideoBlock
                  data={block.data}
                  onChange={() => {}}
                  onDelete={() => {}}
                  isPreview={true}
                />
              )}
              {block.type === 'image' && (
                <ImageBlock
                  data={block.data}
                  onChange={() => {}}
                  onDelete={() => {}}
                  isPreview={true}
                />
              )}
              {block.type === 'text' && (
                <RichTextBlock
                  data={block.data}
                  onChange={() => {}}
                  onDelete={() => {}}
                  isPreview={true}
                />
              )}
              {block.type === 'youtube' && (
                <YouTubeBlock
                  data={block.data}
                  onChange={() => {}}
                  onDelete={() => {}}
                  isPreview={true}
                />
              )}
              {block.type === 'cta' && (
                <CTAButtonBlock
                  data={block.data}
                  onChange={() => {}}
                  onDelete={() => {}}
                  isPreview={true}
                />
              )}
              {block.type === 'price' && (
                <PriceBlock
                  data={block.data}
                  onChange={() => {}}
                  onDelete={() => {}}
                  isPreview={true}
                />
              )}
              {block.type === 'review' && (
                <ReviewBlock
                  data={block.data}
                  onChange={() => {}}
                  onDelete={() => {}}
                  isPreview={true}
                />
              )}
              {block.type === 'faq' && (
                <FAQBlock
                  data={block.data}
                  onChange={() => {}}
                  onDelete={() => {}}
                  isPreview={true}
                />
              )}
              {block.type === 'countdown' && (
                <CountdownBlock
                  data={block.data}
                  onChange={() => {}}
                  onDelete={() => {}}
                  isPreview={true}
                />
              )}
              {block.type === 'contact' && (
                <ContactFormBlock
                  data={block.data}
                  onChange={() => {}}
                  onDelete={() => {}}
                  isPreview={true}
                  salePageId={salePageId || undefined}
                />
              )}
              {block.type === 'gallery' && (
                <GalleryBlock
                  data={block.data}
                  onChange={() => {}}
                  onDelete={() => {}}
                  isPreview={true}
                />
              )}
              {block.type === 'comparison' && (
                <ComparisonTableBlock
                  data={block.data}
                  onChange={() => {}}
                  onDelete={() => {}}
                  isPreview={true}
                />
              )}
              {block.type === 'icontext' && (
                <IconTextBlock
                  data={block.data}
                  onChange={() => {}}
                  onDelete={() => {}}
                  isPreview={true}
                />
              )}
              {block.type === 'divider' && (
                <DividerBlock
                  data={block.data}
                  onChange={() => {}}
                  onDelete={() => {}}
                  isPreview={true}
                />
              )}
              {block.type === 'spacer' && (
                <SpacerBlock
                  data={block.data}
                  onChange={() => {}}
                  onDelete={() => {}}
                  isPreview={true}
                />
              )}
              {block.type === 'payment' && (
                <PaymentBlock
                  data={block.data}
                  onChange={() => {}}
                  onDelete={() => {}}
                  isPreview={true}
                  salePageId={salePageId || undefined}
                  salePageSlug={salePageData.slug}
                  comparisonTableData={salePageData.blocks.find(b => b.type === 'comparison')?.data}
                />
              )}
            </div>
          ))}
          </div>

          {/* Branding */}
          <a href="https://saledi.web.app" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 11.5, color: '#8AA6A8', paddingTop: 8, textDecoration: 'none' }}>
            สร้างด้วย
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'Anuphan',sans-serif", fontWeight: 700, color: '#5B7D80' }}>
              <span style={{ width: 14, height: 14, borderRadius: 5, background: 'linear-gradient(135deg, #00B4A1, #3A93B9)', display: 'inline-flex' }} />
              SaleDi
            </span>
          </a>
        </main>

        {/* Floating contact FAB */}
        {hasAnyLink && (
          <div style={{ position: 'fixed', right: 'clamp(14px,3vw,22px)', bottom: 'calc(env(safe-area-inset-bottom,0px) + 18px)', zIndex: 60, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 11 }}>
            {(salePageData.socialLinks?.facebook || salePageData.socialLinks?.instagram || salePageData.socialLinks?.line) && (
              <button type="button" onClick={() => setFabOpen((v) => !v)} aria-label="ย่อ/ขยายเมนู" style={{ width: 36, height: 36, borderRadius: 999, background: '#0E3A40', color: '#fff', border: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 20px -10px rgba(14,58,64,0.5)', cursor: 'pointer' }}>
                {fabOpen ? <ChevronDown style={{ width: 17, height: 17 }} /> : <ChevronUp style={{ width: 17, height: 17 }} />}
              </button>
            )}
            {fabOpen && (
              <>
                {socialProfileUrl('facebook', salePageData.socialLinks?.facebook) && (
                  <a href={socialProfileUrl('facebook', salePageData.socialLinks?.facebook)!} target="_blank" rel="noopener noreferrer" title="Facebook" aria-label="Facebook" style={{ width: 50, height: 50, borderRadius: 999, background: '#3A93B9', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 24px -10px rgba(58,147,185,0.6)' }}>
                    <Facebook style={{ width: 20, height: 20 }} />
                  </a>
                )}
                {socialProfileUrl('instagram', salePageData.socialLinks?.instagram) && (
                  <a href={socialProfileUrl('instagram', salePageData.socialLinks?.instagram)!} target="_blank" rel="noopener noreferrer" title="Instagram" aria-label="Instagram" style={{ width: 50, height: 50, borderRadius: 999, background: '#5B6FCB', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 24px -10px rgba(91,111,203,0.6)' }}>
                    <Instagram style={{ width: 20, height: 20 }} />
                  </a>
                )}
                {socialProfileUrl('line', salePageData.socialLinks?.line) && (
                  <a href={socialProfileUrl('line', salePageData.socialLinks?.line)!} target="_blank" rel="noopener noreferrer" title="LINE" aria-label="LINE" style={{ width: 50, height: 50, borderRadius: 999, background: '#00B4A1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 24px -10px rgba(0,180,161,0.6)' }}>
                    <MessageCircle style={{ width: 20, height: 20 }} />
                  </a>
                )}
              </>
            )}
            {salePageData.socialLinks?.phone && (
              <a href={`tel:${salePageData.socialLinks.phone}`} title="โทร" aria-label="โทร" style={{ width: 58, height: 58, borderRadius: 999, background: 'linear-gradient(135deg, #FF7A45, #FF9466)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 16px 30px -10px rgba(255,122,69,0.65)' }}>
                <Phone style={{ width: 23, height: 23 }} />
              </a>
            )}
          </div>
        )}
      </div>
  );
};

export default SalePagePublic;
